import React, { ReactElement, useEffect } from 'react';
import { useGetQuery, useUpdateEntite } from '../../../Utils/api';
import { Badge, Button, ButtonGroup, Col, Dropdown, Row } from 'react-bootstrap';
import { useAlertDialog } from '../../../Utils/dialogContext';
import { Produit } from '../../../../../common/src/entities';
import { CellProps } from 'react-table';
import { Colonne } from '../Utils/TypesTableSimple';
import PopoverSimple from '../../Common/PopoverSimple';
import { BiPlus } from 'react-icons/bi';
import { LinkContainer } from 'react-router-bootstrap';
import EnteteDePage from '../Utils/EnteteDePage';
import FiltreActif from '../Utils/FiltreActif';
import TableSimple from '../Utils/TableSimple';
import { useNavigate } from 'react-router-dom';
import DynamicTable from './DynamicTable';
import DynamicTableActivite from './DynamicTableActivite';
import DynamicTableCommission from './DynamicTableCommission';
import {
    errorPrice,
    clientPrice,
    getCurrentPrice,
    affichageComms,
    getTotalSurfacturation,
    getTotalCommission,
} from './UtilsFunctionsProduits';
import { dateFormatter } from '../Utils/Log';

export default function Produits(): ReactElement {
    const listProduit = useGetQuery<Produit[]>('produits/dashboard');
    const navigate = useNavigate();
    const [modifierProduit] = useUpdateEntite(Produit);
    const bsAlert = useAlertDialog();

    useEffect(() => {
        document.title = 'Produits - Dashboard';
    }, []);

    const colonnes: Colonne<Produit>[] = [
        {
            Header: 'Actif',
            accessor: 'actif',
            Cell: function CelluleActif({ value }: CellProps<Produit>) {
                return value ? <Badge variant="light">Actif</Badge> : <Badge variant="secondary">Inactif</Badge>;
            },
            filter: 'equals',
        },
        {
            Header: 'Nom',
            Cell: function CelluleActif({ row }: CellProps<Produit>) {
                return row.original.name;
            },
            sortType: (rowA, rowB) => {
                return rowA.original.name.localeCompare(rowB.original.name, 'fr-CA');
            },
        },
        {
            Header: 'Fournisseur',
            accessor: (row) => {
                if (row.fournisseur) return row.fournisseur.name;
                else return null;
            },
        },
        {
            Header: 'Prix Client',
            Cell: function CelluleContacts({ row }: CellProps<Produit>) {
                const currentPrice = getCurrentPrice(row.original.historique_prix_produit);
                if (typeof currentPrice === 'undefined') return errorPrice();
                else return Number(clientPrice(currentPrice)).toFixed(2) + ' $';
            },
        },

        {
            Header: 'Méthode facturation',
            accessor: (row) => {
                const currentPrice = getCurrentPrice(row.historique_prix_produit);
                if (!currentPrice) return errorPrice();
                return currentPrice.facturation?.methode_facturation;
            },
        },

        {
            Header: 'Commission',
            Cell: function CelluleContacts({ row }: CellProps<Produit>) {
                const title2 = 'Les différents types de commisions de ' + row.original.name;
                const currentPrice = getCurrentPrice(row.original.historique_prix_produit);
                if (!currentPrice) {
                    return errorPrice();
                }
                return row.original.historique_prix_produit ? (
                    <PopoverSimple
                        inList
                        texte={
                            <>
                                <DynamicTableCommission title={title2} data={currentPrice} />
                            </>
                        }
                    >
                        <u>{affichageComms(currentPrice) as any}</u>
                    </PopoverSimple>
                ) : null;
            },
        },
        {
            Header: 'Profit $',
            accessor: (row) => {
                const currentPrice = getCurrentPrice(row.historique_prix_produit);
                if (!currentPrice) return errorPrice();
                return `${(
                    Number(getTotalSurfacturation(currentPrice)) + Number(getTotalCommission(currentPrice))
                ).toFixed(2)} $`;
            },
        },
        {
            Header: '# Prix',
            Cell: function CelluleContacts({ row }: CellProps<Produit>) {
                const title2 = 'Historique des prix du produit ' + row.original.name;
                const length = row.original.historique_prix_produit.length;
                return row.original.historique_prix_produit ? (
                    <>
                        <PopoverSimple
                            inList
                            placement="bottom"
                            texte={
                                <>
                                    <DynamicTable title={title2} data={row.original.historique_prix_produit} />
                                </>
                            }
                        >
                            <u>{length as any}</u>
                        </PopoverSimple>
                    </>
                ) : null;
            },
        },

        {
            Header: 'Activités reliées',
            Cell: function CelluleContacts({ row }: CellProps<Produit>) {
                const title2 = 'Listes des activités reliées à ' + row.original.name;
                return row.original.activiteProduits?.length > 0 ? (
                    <>
                        <PopoverSimple
                            inList
                            texte={
                                <>
                                    <DynamicTableActivite title={title2} data={row.original.activiteProduits} />
                                </>
                            }
                        >
                            <u>{row.original.activiteProduits.length as any}</u>
                        </PopoverSimple>
                    </>
                ) : (
                    <span>0</span>
                );
            },
        },

        {
            Header: 'Dernière modification',
            accessor: (row) => {
                // Convertissez la chaîne de caractères en objet Date
                if (row.date_derniere_modification) {
                    return dateFormatter(row.date_derniere_modification);
                } else {
                    return 'N/A';
                }
            },
        },
    ];

    const toggleActif = async (p: Produit) => {
        if (window.confirm(`Etes-vous sur de vouloir ${p.actif ? 'désactiver' : 'réactiver'} ce produit?`)) {
            try {
                await modifierProduit({ id: p.id, actif: !p.actif });
                window.location.reload();
            } catch (error) {
                const { response } = error;
                if (response.status === 409) {
                    bsAlert(
                        `D'autres entités dépendent de ce produit, impossible de le ${
                            p.actif ? 'désactiver' : 'réactiver'
                        }`,
                        'warning',
                    );
                } else {
                    bsAlert(
                        `Erreur dans la ${p.actif ? 'désactivation' : 'réactivation'} du fournisseur: ${error.message}`,
                        'danger',
                    );
                }
            }
        }
    };
    return (
        <>
            <Row>
                <Col>
                    <EnteteDePage texte="Produits" cacherBoutonRetour />
                </Col>
                <Col xs="auto">
                    <ButtonGroup>
                        <LinkContainer to="new">
                            <Button size="sm" variant="outline-primary" className="d-flex align-items-center">
                                <BiPlus /> Ajouter un produit
                            </Button>
                        </LinkContainer>
                    </ButtonGroup>
                </Col>
            </Row>
            <TableSimple
                colonnes={colonnes}
                resultat={listProduit}
                lien="id"
                hover
                affichageMenu={(produit: Produit) => (
                    <>
                        <Dropdown.Item onClick={() => navigate(String(produit.id))}>Modifier</Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => toggleActif(produit)}
                            className={produit.actif ? 'text-danger' : 'text-success'}
                        >
                            {produit.actif ? 'Désactiver' : 'Restaurer'}
                        </Dropdown.Item>
                    </>
                )}
                affichageFiltres={FiltreActif}
                etatInitial={{
                    filters: [
                        {
                            id: 'actif',
                            value: true,
                        },
                    ],
                    sortBy: [
                        {
                            id: 'name',
                        },
                    ],
                    hiddenColumns: ['actif'],
                }}
            />
        </>
    );
}
